import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { createIsomorphicFn } from '@tanstack/react-start';
import { getRequestHeader } from '@tanstack/react-start/server';
import { PageShell } from '~/components/layout';
import { Container } from '~/components/ui';
import { APP_STORE_URL, PLAY_STORE_URL, storeUrlForUserAgent } from '~/lib/appStores';

const getAppDownloadUserAgent = createIsomorphicFn()
  .server(() => getRequestHeader('user-agent') ?? '')
  .client(() => navigator.userAgent);

/**
 * Smart app-download link. QR codes and shared URLs point here; we redirect
 * iOS → App Store and Android → Play Store based on the request User-Agent.
 * Desktop (or unknown) visitors see a small chooser with both store links.
 */
export const Route = createFileRoute('/get-the-app')({
  head: () => ({
    meta: [
      { title: 'Get the nsave app' },
      {
        name: 'description',
        content: 'Download nsave on the App Store or Google Play.',
      },
      { name: 'robots', content: 'noindex,follow' },
    ],
  }),
  beforeLoad: () => {
    const href = storeUrlForUserAgent(getAppDownloadUserAgent());
    if (href) throw redirect({ href });
  },
  component: GetTheAppPage,
});

function GetTheAppPage() {
  return (
    <PageShell>
      <section className="py-24 md:py-32">
        <Container className="flex max-w-md flex-col items-center gap-8 text-center">
          <h1 className="text-h3 text-ink">Get the app</h1>
          <p className="text-body text-grey-700">
            Download nsave on your phone from the App Store or Google Play.
          </p>
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
            <a
              href={APP_STORE_URL}
              className="inline-flex items-center justify-center rounded-pill bg-red-500 px-5 py-3 text-[16px] font-medium text-white hover:bg-red-700">
              App Store
            </a>
            <a
              href={PLAY_STORE_URL}
              className="inline-flex items-center justify-center rounded-pill bg-grey-50 px-5 py-3 text-[16px] font-medium text-ink hover:bg-grey-100">
              Google Play
            </a>
          </div>
          <Link to="/" className="text-body text-grey-700 underline-offset-2 hover:underline">
            Back to home
          </Link>
        </Container>
      </section>
    </PageShell>
  );
}
